/**
 * ShareMenuItem - Menu item that opens the ShareDialog.
 *
 * Use this in dropdown/context menus to provide sharing functionality.
 */
import { useState } from 'react';
import { Share2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DropdownMenuItem } from '../../../components/ui/dropdown-menu';
import { ShareDialog } from './ShareDialog';
import { useSharingChromeVisible } from './usePermissions';
import type { RelationType } from './types';

interface ShareMenuItemProps {
  /** Entity type in format module.entity (e.g., 'tasks.board') */
  objectType: string;
  /** Entity UUID */
  objectId: string;
  /** Display name for the entity (shown in dialog title) */
  entityName?: string;
  /** Relations that can be granted (defaults to all) */
  allowedRelations?: RelationType[];
  /** Whether user can share (has owner/share permission) */
  canShare?: boolean;
  /** Callback when menu item is clicked (useful for closing parent menu) */
  onMenuClose?: () => void;
  /** Disabled state */
  disabled?: boolean;
}

export function ShareMenuItem({
  objectType,
  objectId,
  entityName,
  allowedRelations,
  canShare = true,
  onMenuClose,
  disabled = false,
}: ShareMenuItemProps) {
  const { t } = useTranslation('common');
  const [dialogOpen, setDialogOpen] = useState(false);
  const chromeVisible = useSharingChromeVisible(objectType);

  const handleDialogClose = () => {
    setDialogOpen(false);
    onMenuClose?.();
  };

  // Hide the Share menu item for a configurable object_type left on `everyone`
  // (sharing is a no-op there — §9 C3). Legacy types / loading / error: fail open.
  if (!chromeVisible) {
    return null;
  }

  return (
    <>
      <DropdownMenuItem
        onSelect={(e) => {
          // Prevent dropdown from auto-closing, which would unmount
          // this component and destroy the dialog state
          e.preventDefault();
          setDialogOpen(true);
        }}
        disabled={disabled}
      >
        <Share2 className="mr-2 h-4 w-4" />
        <span>{t('sharing.share')}</span>
      </DropdownMenuItem>

      <ShareDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        objectType={objectType}
        objectId={objectId}
        entityName={entityName}
        allowedRelations={allowedRelations}
        canShare={canShare}
      />
    </>
  );
}
